const AppRoutes = {
  admin: {
    root: "/admin",
    /** "JourneyManagement" can be considered old/v1, before the admin UI was introduced for editing journey flows in 2024  */
    journeyManagement: {
      path: "/admin/client-journeys",
      sendingHistory: "/admin/client-journeys/sending-history",
      config: {
        path: "/admin/client-journeys/config/:clientFormId",
        generateRoute: (clientFormId: string): string => {
          return AppRoutes.admin.journeyManagement.config.path.replace(
            ":clientFormId",
            clientFormId,
          );
        },
      },
      oldSend: {
        path: "/admin/client-journeys/old-send/:clientFormId",
        generateRoute: (clientFormId: string): string => {
          return AppRoutes.admin.journeyManagement.oldSend.path.replace(
            ":clientFormId",
            clientFormId,
          );
        },
      },
      create: {
        path: "/admin/client-journeys/create",
      },
      send: {
        path: "/admin/client-journeys/send/:journeyId",
        generateRoute: (journeyId: string): string => {
          return AppRoutes.admin.journeyManagement.send.path.replace(
            ":journeyId",
            journeyId,
          );
        },
      },
    },
    dashboards: {
      path: "/admin/dashboards",
    },
    journeys: {
      root: "/admin/journeys",
      category: {
        path: "/admin/journeys/:categoryId",
        generateRoute: (categoryId: string): string => {
          return AppRoutes.admin.journeys.category.path.replace(
            ":categoryId",
            categoryId,
          );
        },
      },
      journey: {
        path: "/admin/journeys/:categoryId/:journeyId",
        generateRoute: (categoryId: string, journeyId: string): string => {
          return AppRoutes.admin.journeys.journey.path
            .replace(":categoryId", categoryId)
            .replace(":journeyId", journeyId);
        },
      },
    },
    userManagement: {
      path: "/admin/user-management",
      userDetail: {
        path: "/admin/user-management/user/:userId",
        generateRoute: (userId: string): string => {
          return AppRoutes.admin.userManagement.userDetail.path.replace(
            ":userId",
            userId,
          );
        },
        adminHistory: "/admin/user-management/user/:userId/admin-history",
        generateAdminHistoryRoute: (userId: string): string => {
          return AppRoutes.admin.userManagement.userDetail.adminHistory.replace(
            ":userId",
            userId,
          );
        },
      },
    },
    dataHealth: {
      path: "/admin/data-health",
    },
    userGroups: {
      root: "/admin/user-groups",
      manage: {
        path: "/admin/user-groups/:id",
        pathWithId: "/admin/user-groups/:id",
        generateRoute: (id: string): string => {
          return AppRoutes.admin.userGroups.manage.pathWithId.replace(
            ":id",
            id,
          );
        },
      },
    },
    roles: {
      path: "/admin/roles",
      manageRole: {
        path: "/admin/roles/manage/:roleId",
        generateRoute: (roleId: string): string => {
          return AppRoutes.admin.roles.manageRole.path.replace(
            ":roleId",
            roleId,
          );
        },
        generateRouteWithView: (
          roleId: string,
          view: "details" | "members",
        ): string => {
          const basePath = AppRoutes.admin.roles.manageRole.path.replace(
            ":roleId",
            roleId,
          );
          return `${basePath}?view=${view}`;
        },
      },
      logs: {
        path: "/admin/roles/logs",
        pathWithRoleId: "/admin/roles/logs/:roleId",
        generateRoute: (): string => {
          return AppRoutes.admin.roles.logs.path;
        },
        generateRouteWithRoleId: (roleId: string): string => {
          return AppRoutes.admin.roles.logs.pathWithRoleId.replace(
            ":roleId",
            roleId,
          );
        },
      },
      assignmentsByUser: {
        path: "/admin/roles/assignments-by-user",
      },
    },
    datapoints: {
      root: "/admin/datapoints",
      manage: {
        path: "/admin/datapoints/:id",
        generateRoute: (id: string): string => {
          return AppRoutes.admin.datapoints.manage.path.replace(":id", id);
        },
      },
    },
    notifications: {
      root: "/admin/notifications",
      create: "/admin/notifications/create",
      manage: {
        path: "/admin/notifications/manage/:notificationId",
        generateRoute: (notificationId: number): string => {
          return `/admin/notifications/manage/${notificationId}`;
        },
      },
    },
    brands: {
      path: "/admin/brands",
    },
    platformConfiguration: {
      path: "/admin/platform-configuration",
    },
    simpleForms: {
      root: "/admin/simple-forms",
      manage: {
        path: "/admin/simple-forms/:id",
        generateRoute: (id: string): string => {
          return AppRoutes.admin.simpleForms.manage.path.replace(":id", id);
        },
      },
    },
    advancedTaskTypes: {
      root: "/admin/advanced-tasks",
      manage: {
        path: "/admin/advanced-tasks/:id",
        generateRoute: (id: string): string => {
          return AppRoutes.admin.advancedTaskTypes.manage.path.replace(
            ":id",
            id,
          );
        },
      },
    },
    restricted: {
      root: "/admin/restricted",
    },
    analyticsPages: {
      root: "/admin/analytics-pages",
      manage: {
        path: "/admin/analytics-pages/:pageId",
        generateRoute: (pageId: number): string =>
          `/admin/analytics-pages/${pageId}`,
      },
    },
    sub: {
      journeyManagement: {
        path: "client-journeys",
        sendingHistory: "client-journeys/sending-history",
        oldSend: {
          path: "/client-journeys/old-send/:clientFormId",
          generateRoute: (clientFormId: string): string => {
            return AppRoutes.admin.sub.journeyManagement.oldSend.path.replace(
              ":clientFormId",
              clientFormId,
            );
          },
        },
        config: {
          path: "/client-journeys/config/:clientFormId",
          generateRoute: (clientFormId: string): string => {
            return AppRoutes.admin.journeyManagement.config.path.replace(
              ":clientFormId",
              clientFormId,
            );
          },
        },
        create: {
          path: "/client-journeys/create",
        },
        send: {
          path: "/client-journeys/send/:journeyId",
          generateRoute: (journeyId: string): string => {
            return AppRoutes.admin.sub.journeyManagement.send.path.replace(
              ":journeyId",
              journeyId,
            );
          },
        },
      },
      dashboards: {
        path: "dashboards",
      },
      journeys: {
        root: "journeys",
        category: "journeys/:categoryId",
        journey: "journeys/:categoryId/:journeyId",
      },
      userManagement: {
        path: "user-management",
        userDetail: {
          path: "/user-management/user/:userId",
          generateRoute: (userId: string): string => {
            return AppRoutes.admin.userManagement.userDetail.path.replace(
              ":userId",
              userId,
            );
          },
          adminHistory: "/user-management/user/:userId/admin-history",
          generateAdminHistoryRoute: (userId: string): string => {
            return AppRoutes.admin.userManagement.userDetail.adminHistory.replace(
              ":userId",
              userId,
            );
          },
        },
      },
      dataHealth: {
        path: "data-health",
      },
      userGroups: {
        root: "user-groups",
        manage: "user-groups/:id",
      },
      roles: {
        root: "roles",
        manage: "roles/manage/:roleId",
        generateManageRouteWithView: (
          roleId: string,
          view: "details" | "members",
        ): string => {
          return `roles/manage/${roleId}?view=${view}`;
        },
        logs: "roles/logs",
        logsWithRoleId: "roles/logs/:roleId",
        assignmentsByUser: "roles/assignments-by-user",
      },
      brands: {
        path: "brands",
        edit: "brands/:brandId/edit",
      },
      platformConfiguration: {
        path: "platform-configuration",
      },
      simpleForms: {
        root: "simple-forms",
        manage: "simple-forms/:id",
      },
      advancedTaskTypes: {
        root: "advanced-tasks",
        manage: "advanced-tasks/:id",
        legacyRoot: "advanced-task-types",
        legacyManage: "advanced-task-types/:id",
      },
      restricted: {
        path: "restricted",
        legacySecretStuff: "secret-stuff",
      },
      analyticsPages: {
        root: "analytics-pages",
        manage: "analytics-pages/:pageId",
      },
      datapoints: {
        root: "datapoints",
        manage: "datapoints/:id",
      },
      notifications: {
        root: "notifications",
        create: "notifications/create",
        manage: "notifications/manage/:notificationId",
      },
    },
    oldSystemUserManagement: "/og-app/admin",
    classicUserDetailAdmin: {
      path: "/og-app/admin/:userId",
      generateRoute: (userId: string): string => {
        return AppRoutes.admin.classicUserDetailAdmin.path.replace(
          ":userId",
          userId,
        );
      },
    },
  },
  analytics: {
    root: "/analytics",
    classic: "/og-app/analytics",
    usage: "/analytics/usage",
    dynamicPage: {
      path: "/analytics/page/:pageId",
      generateRoute: (pageId: string): string => {
        return AppRoutes.analytics.dynamicPage.path.replace(":pageId", pageId);
      },
    },
    exports: "/analytics/exports",
    scoreUploader: "/analytics/score-uploader",
    aiQuery: "/analytics/ai-query",
    sub: {
      classic: "classic",
      dynamicPage: {
        path: "page/:pageId",
        generateRoute: (pageId: string): string => {
          return AppRoutes.analytics.sub.dynamicPage.path.replace(
            ":pageId",
            pageId,
          );
        },
      },
      usage: "usage",
      exports: "exports",
      scoreUploader: "score-uploader",
      aiQuery: "ai-query",
    },
  },
  companyDashboard: "/og-app/dashboard",
  demo: {
    managerReset: "/demo/manager-reset",
  },
  reviewHistory: {
    path: "/og-app/review-history",
    search: "?displayType=1",
  },
  legacyTaskList: {
    path: "/og-app/tasks",
    search: "?displayType=1",
  },
  collaborativeDocument: {
    standardPath: "/collab-doc/:answerSetUniqueId",
    specificFormPath: "/collab-doc/:answerSetUniqueId/:singleFormId",
    viewCollabDoc: "/view-collab-doc/:answerSetUniqueId",
    generateRoute: (
      answerSetUniqueId: string,
      showPostJourneyModal: boolean,
      specificClientFormId: number | null,
    ): string => {
      const hasClientFormId = specificClientFormId && specificClientFormId > 0;
      const path = hasClientFormId
        ? AppRoutes.collaborativeDocument.specificFormPath
        : AppRoutes.collaborativeDocument.standardPath;

      let output = path.replace(":answerSetUniqueId", answerSetUniqueId);

      if (hasClientFormId) {
        output = output.replace(
          ":singleFormId",
          specificClientFormId.toString(),
        );
      }

      if (showPostJourneyModal) {
        output += "?pj=1";
      }

      return output;
    },
  },
  yourJourney: {
    root: "/me",
    conversations: "/me/conversations",
    pulseChecks: "/me/pulse-checks",
    catchUps: "/me/catch-ups",
    tasks: {
      path: "/me/tasks/:taskTypeId",
      generatePathRoute: (taskTypeId: string): string => {
        return AppRoutes.yourJourney.tasks.path.replace(
          ":taskTypeId",
          taskTypeId,
        );
      },
      pathWithTaskId: "/me/tasks/:taskTypeId/:taskId",
      generatePathWithTaskIdRoute: (
        taskTypeId: string,
        taskId: string,
      ): string => {
        return AppRoutes.yourJourney.tasks.pathWithTaskId
          .replace(":taskTypeId", taskTypeId)
          .replace(":taskId", taskId);
      },
    },
    sub: {
      catchUps: {
        path: "/catch-ups",
      },
      conversations: {
        path: "/conversations",
      },
      pulseChecks: {
        path: "/pulse-checks",
      },
      tasks: {
        path: "/tasks/:taskTypeId",
        pathWithTaskId: "/tasks/:taskTypeId/:taskId",
      },
    },
  },
  yourPeople: {
    root: "/team",
    pulseChecks: "/team/pulse-checks",
    conversations: {
      path: "/team/conversations",
      pathWithPreselectedEmployeeId:
        "/team/conversations/:preselectedEmployeeId",
      generatePathWithPreselectedEmployeeIdRoute: (
        preselectedEmployeeId: number,
      ): string => {
        return AppRoutes.yourPeople.conversations.pathWithPreselectedEmployeeId.replace(
          ":preselectedEmployeeId",
          preselectedEmployeeId.toString(),
        );
      },
    },
    tasks: {
      path: "/team/tasks/:taskTypeId",
      generateRoute: (taskTypeId: string): string => {
        return AppRoutes.yourPeople.tasks.path.replace(
          ":taskTypeId",
          taskTypeId,
        );
      },
      pathWithPreselectedEmployeeId:
        "/team/tasks/:taskTypeId/:preselectedEmployeeId",
      generatePathWithPreselectedEmployeeIdRoute: (
        taskTypeId: string,
        preselectedEmployeeId: number,
      ): string => {
        return AppRoutes.yourPeople.tasks.pathWithPreselectedEmployeeId
          .replace(":taskTypeId", taskTypeId)
          .replace(":preselectedEmployeeId", preselectedEmployeeId.toString());
      },
    },
    sub: {
      tasks: {
        path: "/tasks/:taskTypeId",
        pathWithPreselectedEmployeeId:
          "/tasks/:taskTypeId/:preselectedEmployeeId",
      },
      conversations: {
        path: "/conversations",
        pathWithPreselectedEmployeeId: "/conversations/:preselectedEmployeeId",
      },
      pulseChecks: "/pulse-checks",
    },
  },
  componentsDemo: "/components-demo",
  help: "https://help.talenttoolbox.com/",
  /** HelpScout article path under AppRoutes.help (ADR-0006 Data snapshot). */
  helpAnalyticsHistoricalModeArticle:
    "article/248-analytics-filters-historical-mode",
  get helpAnalyticsHistoricalMode() {
    return `${this.help}${this.helpAnalyticsHistoricalModeArticle}`;
  },
  home: "/",
  auth: {
    callback: "/auth/callback",
    ssoCallback: "/auth/sso-callback",
    logout: "/auth/logout",
    remoteLogoutCallback: "/auth/session-ended",
    postLogoutRedirectUri: function (
      mvcAppRootUrl: string,
      triggeredByRemoteApp: boolean,
      clientId: number | undefined,
    ) {
      let redirectUrl: string;
      if (triggeredByRemoteApp) {
        // Logout process has already been done in MVC TT, and now in the React app too, so pass back to TT to redirect appropriately
        redirectUrl = `${mvcAppRootUrl}${this.remoteLogoutCallback}`;

        // If there is external SSO involved, pass the client id back so MVC TT can handle that
        if (clientId) {
          redirectUrl += `?clientId=${clientId}`;
        }
      } else {
        // Logout has begun in the React app, so now we need to log out of TT as well
        redirectUrl = `${mvcAppRootUrl}/auth/logout?skipRemote=1`;
      }

      return redirectUrl;
    },
  },
  profile: "/og-app/profile",
  changePassword: "/og-app/changePassword",
  yourGoals: "/og-app/yourGoals",
  notificationPreferences: "/og-app/notificationPreferences",
  logout: "/og-app/logout",
};

export default AppRoutes;
